import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Truck, 
  Globe, 
  Clock, 
  Shield, 
  ArrowRight, 
  Mail,
  CheckCircle,
  Navigation
} from 'lucide-react'

const Home = () => {
  const services = [
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Transport międzynarodowy",
      description: "Regularne i sprawne przewozy ładunków na trasach między Polską, Szwajcarią a Niemcami.",
      color: "from-blue-600 to-indigo-600"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Logistyka i spedycja",
      description: "Kompleksowe zarządzanie łańcuchem dostaw oraz doradztwo w planowaniu tras transgranicznych.",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Transport ekspresowy",
      description: "Szybkie i gwarantowane terminowo dostawy do klienta w systemie dedykowanym.",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Kierowcy zawodowi",
      description: "Doświadczony zespół wykwalifikowanych kierowców z pełnymi uprawnieniami i znajomością przepisów.",
      color: "from-emerald-500 to-teal-600"
    }
  ]

  const stats = [
    { value: "7+", label: "Lat doświadczenia" },
    { value: "100%", label: "Terminowości dostaw" },
    { value: "PL ↔ CH", label: "Specjalizacja tras" },
    { value: "24/7", label: "Wsparcie i logistyka" }
  ]

  const advantages = [
    "Ponad 7 lat doświadczenia w spedycji międzynarodowej (od 2018 roku)",
    "Dogłębna znajomość szwajcarskich oraz unijnych przepisów celnych i transportowych",
    "Współpraca z wiodącymi europejskimi partnerami logistycznymi",
    "Nowoczesne narzędzia telemetryczne oraz automatyzacja zarządzania flotą",
    "Elastyczność i dopasowanie warunków frachtu do wymogów każdego klienta",
    "Szybka realizacja procedur i pełna transparentność dokumentów"
  ]

  return (
    <div className="min-h-screen bg-slate-50/50 overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-850 to-primary-950 text-white py-24 sm:py-32">
        {/* Glow Spheres */}
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-secondary-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-primary-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="container-max px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 space-y-6"
            >
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-white/5 backdrop-blur-md border border-white/10 py-1.5 px-4 rounded-full text-xs font-bold text-secondary-300 uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-secondary-400 animate-pulse"></span>
                <span>Transport & Logistyka Polska ↔ Szwajcaria</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-extrabold font-display tracking-tight leading-tight">
                FG Falke
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-secondary-400 via-secondary-300 to-white">
                  Bezpieczny Transport i Spedycja
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-primary-200/90 font-normal leading-relaxed max-w-2xl">
                Profesjonalne i rzetelne usługi spedycyjne w sercu Europy. 
                Specjalizujemy się w obsłudze tras transgranicznych oraz doradztwie transportowym (CKZ).
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/contact" className="btn-secondary text-sm px-8 py-4 inline-flex items-center justify-center">
                  Skontaktuj się z nami
                  <ArrowRight className="ml-2.5 w-4 h-4" />
                </Link>
                <Link to="/services" className="btn-outline text-sm px-8 py-4 inline-flex items-center justify-center">
                  Zobacz nasze usługi
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Premium Image with Stats */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 group bg-slate-900">
                <img 
                  src="/swiss_alpine_truck.png" 
                  alt="FG Falke Alpine Transport" 
                  className="w-full h-[320px] md:h-[380px] object-cover opacity-85 group-hover:scale-105 transition-transform duration-700 pointer-events-none"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent"></div>
                
                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 space-y-4 text-white z-10">
                  <h3 className="text-xl font-bold font-display leading-tight text-white">Dlaczego warto nam zaufać?</h3>
                  <p className="text-slate-350 text-xs leading-relaxed max-w-sm hidden sm:block">
                    Nasze doświadczenie na trasach w Szwajcarii i Niemczech gwarantuje bezpieczeństwo towarów oraz pełną zgodność z procedurami celnymi.
                  </p>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-4">
                    {stats.map((s, idx) => (
                      <div key={idx} className="space-y-0.5">
                        <div className="text-xl md:text-2xl font-extrabold text-secondary-300 font-display leading-tight">{s.value}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-300 leading-none">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <span className="text-xs font-extrabold text-secondary-600 uppercase tracking-widest bg-secondary-50 py-1.5 px-4 rounded-full">Nasza Specjalizacja</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4 mb-6">
              Kompleksowe Usługi Logistyczne
            </h2>
            <p className="text-lg text-slate-500 leading-relaxed">
              Oferujemy niezawodne i dostosowane rozwiązania transportowe, które pozwalają naszym partnerom biznesowym optymalizować koszty dostaw międzynarodowych.
            </p>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((s, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-md card-interactive relative overflow-hidden group"
              >
                {/* Accent Color Bar */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${s.color}`}></div>
                
                {/* Icon Circle */}
                <div className={`bg-gradient-to-br ${s.color} text-white w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-md shadow-slate-200/50 group-hover:scale-110 transition-transform duration-300`}>
                  {s.icon}
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 font-display mb-3 leading-tight group-hover:text-primary-600 transition-colors">
                  {s.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {s.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-slate-50 relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Column - Copy */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-6"
            >
              <span className="text-xs font-extrabold text-primary-600 uppercase tracking-widest bg-primary-100/60 py-1.5 px-4 rounded-full">O nas</span>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4">
                Partner, Którego Potrzebujesz na Europejskich Drogach
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                FG Falke to dynamicznie rozwijające się przedsiębiorstwo transportowe kierowane przez Mariusza Sokołowskiego. Nasze doświadczenie pozwala nam optymalizować procedury transgraniczne i zapewniać sprawny przepływ towarów.
              </p>
              
              {/* Checkmarks Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {advantages.map((advantage, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm font-medium">{advantage}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Graphical Banner Box */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="bg-gradient-to-br from-primary-800 to-primary-950 text-white rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-secondary-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                  <Navigation className="w-6 h-6 text-secondary-300" />
                </div>
                <h3 className="text-2xl font-bold font-display mb-4">Gwarancja Jakości FG Falke</h3>
                <ul className="space-y-4 text-primary-200/90 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full shrink-0"></span>
                    Ubezpieczenie OCP przewoźnika do wysokich kwot
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full shrink-0"></span>
                    Pełna telemetria GPS w każdym pojeździe
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full shrink-0"></span>
                    Szybkie rozliczenia i pełne faktury VAT
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full shrink-0"></span>
                    Certyfikowany zarządzający transportem (CKZ)
                  </li>
                </ul>
                <div className="mt-8">
                  <Link to="/about" className="text-white hover:text-secondary-300 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors">
                    Dowiedz się więcej o nas
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 to-primary-950 text-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(2,132,199,0.12),transparent_70%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <h2 className="text-3xl md:text-5xl font-extrabold font-display tracking-tight leading-tight">
              Potrzebujesz Niezawodnego Transportu?
            </h2>
            <p className="text-lg md:text-xl text-primary-200/90 leading-relaxed font-light">
              Napisz do nas już dzisiaj! Zespół FG Falke przygotuje dla Twojego przedsiębiorstwa dedykowaną, bezpłatną kalkulację kosztów frachtu.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link to="/contact" className="btn-secondary text-sm px-8 py-4 inline-flex items-center justify-center">
                <Mail className="mr-2.5 w-4 h-4" />
                Skontaktuj się z nami
              </Link>
              <Link to="/pricing" className="btn-outline text-sm px-8 py-4 inline-flex items-center justify-center">
                Sprawdź cennik & CKZ
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home
